import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../services/api';
import { useEspecificacao } from '../contexts/EspecificacaoContext';
import MenuAcoes from '../components/MenuAcoes';
import Folha from '../components/especificacao/Folha';
import EnvioPdf from '../components/especificacao/EnvioPdf';
import { dataCurta, situacaoDe } from '../utils/especificacao';
import '../components/especificacao/especificacao.css';
import './Especificacoes.css';

const NOVA = { nome: '', cliente: '', modo: 'catalogo', arquivo: null };

/**
 * Minhas especificações — uma por projeto/cliente. Duas formas de montar:
 * pelo CATÁLOGO (vira a especificação ativa, onde o "+ Especificar" adiciona) ou
 * enviando o PDF que o arquiteto já fez no programa dele.
 */
export default function Especificacoes() {
  const navigate = useNavigate();
  const { lista, criar, apagarEsp, duplicarEsp, recarregar, ativa, avisar } = useEspecificacao();
  const [carregando, setCarregando] = useState(true);
  const [nova, setNova] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [progresso, setProgresso] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => { recarregar().finally(() => setCarregando(false)); }, [recarregar]);

  const abrirNova = (modo = 'catalogo') => { setErro(''); setProgresso(null); setNova({ ...NOVA, modo }); };
  const podeCriar = nova && nova.nome.trim().length >= 2 && (nova.modo === 'catalogo' || nova.arquivo);

  const criarNova = async () => {
    setSalvando(true); setErro('');
    try {
      const extra = { cliente_nome: nova.cliente.trim() || null, origem: nova.modo === 'pdf' ? 'pdf' : 'catalogo' };
      const id = await criar(nova.nome.trim(), extra);
      if (nova.modo === 'pdf') {
        const fd = new FormData();
        fd.append('arquivo', nova.arquivo);
        setProgresso(0);
        try {
          await api.put(`/especificacoes/${id}/pdf`, fd, {
            onUploadProgress: e => setProgresso(Math.round((e.loaded * 100) / (e.total || nova.arquivo.size))),
          });
        } catch (e) {
          // A especificação já existe: leva pro detalhe, onde dá pra tentar o envio de novo
          await recarregar();
          setNova(null);
          avisar(e.response?.data?.error || 'Não foi possível enviar o PDF — tente de novo na especificação');
          navigate(`/especificacoes/${id}`);
          return;
        }
        await recarregar();
        setNova(null);
        avisar('PDF enviado');
        navigate(`/especificacoes/${id}`);
      } else {
        // Pelo catálogo: a especificação já é a ativa — vai direto escolher os produtos
        setNova(null);
        avisar(`“${nova.nome.trim()}” criada — toque em + Especificar nos produtos`);
        navigate('/catalogo');
      }
    } catch (e) { setErro(e.response?.data?.error || 'Não foi possível criar'); }
    finally { setSalvando(false); setProgresso(null); }
  };

  const apagar = async (e) => {
    const r = await Swal.fire({
      icon: 'warning', title: `Apagar “${e.nome}”?`,
      text: e.origem === 'pdf' ? 'A especificação e o PDF enviado serão apagados. Não dá pra desfazer.'
        : `A lista com ${e.itens} ${e.itens === 1 ? 'item' : 'itens'} será apagada. Não dá pra desfazer.`,
      showCancelButton: true, confirmButtonText: 'Apagar', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#c0392b', reverseButtons: true,
    });
    if (r.isConfirmed) {
      try { await apagarEsp(e.id); }
      catch (err) { Swal.fire({ icon: 'error', title: 'Não foi possível apagar', text: err.response?.data?.error || '' }); }
    }
  };
  const duplicar = async (e) => { const id = await duplicarEsp(e.id); navigate(`/especificacoes/${id}`); };

  return (
    <div className="espl">
      <div className="espl-topo">
        <div>
          <h1 className="cat-titulo">Minhas <span>especificações</span></h1>
          <p className="cat-sub mb-0">A lista de produtos de cada projeto — montada pelo catálogo ou com o PDF que você já fez.</p>
        </div>
      </div>

      {/* Botão flutuante no canto inferior direito — mesmo lugar/modelo da bolha
          da especificação no catálogo (padrão de loja online) */}
      <button type="button" className="fab" onClick={() => abrirNova()} aria-label="Nova especificação">
        <i className="bi bi-plus-lg" />
        <span className="fab-rotulo"><strong>Nova especificação</strong></span>
      </button>

      {carregando && !lista.length ? (
        <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} /></div>
      ) : !lista.length ? (
        <div className="espl-boas-vindas">
          <h2>Como funciona</h2>
          <div className="espl-passos">
            <div><span>1</span><i className="bi bi-grid-1x2" /><strong>Escolha no catálogo</strong><p>Toque em <b>+ Especificar</b> nos produtos do projeto.</p></div>
            <div><span>2</span><i className="bi bi-rulers" /><strong>Quantidades e ambientes</strong><p>Informe quanto vai e onde: “Banheiro social, Cozinha”.</p></div>
            <div><span>3</span><i className="bi bi-file-earmark-pdf" /><strong>PDF com a sua logo</strong><p>Gere o quantitativo pro cliente, organizado por categoria.</p></div>
          </div>
          <p className="espl-ou">Já tem o quantitativo pronto no seu programa? Envie o PDF.</p>
          <div className="d-flex gap-2 flex-wrap justify-content-center">
            <button type="button" className="esp-btn" onClick={() => abrirNova('catalogo')}><i className="bi bi-grid-1x2" />Montar pelo catálogo</button>
            <button type="button" className="esp-btn-sec" onClick={() => abrirNova('pdf')}><i className="bi bi-cloud-arrow-up" />Enviar meu PDF</button>
          </div>
        </div>
      ) : (
        <div className="espl-lista">
          <div className="espl-cab">
            <span>Projeto</span><span>Conteúdo</span><span>Situação</span><span>Atualizada</span><span />
          </div>
          {lista.map(e => {
            const s = situacaoDe(e);
            return (
              <div key={e.id} className={`espl-linha ${ativa?.id === e.id ? 'ativa' : ''}`} role="button" tabIndex={0}
                onClick={() => navigate(`/especificacoes/${e.id}`)}
                onKeyDown={ev => { if (ev.key === 'Enter') navigate(`/especificacoes/${e.id}`); }}>
                <div className="espl-proj">
                  <strong>{e.nome}</strong>
                  <span>{e.cliente_nome || 'Cliente não informado'}{ativa?.id === e.id && <em className="espl-ativa">em uso no catálogo</em>}</span>
                </div>
                <div className="espl-itens">
                  {e.origem === 'pdf'
                    ? <span className="espl-pdf"><i className="bi bi-file-earmark-pdf-fill" />PDF próprio</span>
                    : <><b>{e.itens}</b> {e.itens === 1 ? 'item' : 'itens'}</>}
                </div>
                <div><span className={`espl-sit ${s.classe}`}><i className={`bi ${s.icone}`} />{s.rotulo}</span></div>
                <div className="espl-data">{dataCurta(e.atualizado_em)}</div>
                <div className="text-end" onClick={ev => ev.stopPropagation()} onKeyDown={ev => ev.stopPropagation()} role="presentation">
                  <MenuAcoes itens={[
                    { label: 'Abrir', icone: 'bi-box-arrow-up-right', onClick: () => navigate(`/especificacoes/${e.id}`) },
                    { label: 'Duplicar (projeto parecido)', icone: 'bi-copy', onClick: () => duplicar(e) },
                    { divisor: true },
                    { label: 'Apagar', icone: 'bi-trash3', perigo: true, onClick: () => apagar(e) },
                  ]} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {nova && (
        <Folha
          titulo="Nova especificação"
          onFechar={() => !salvando && setNova(null)}
          largura={540}
          rodape={(
            <>
              <button type="button" className="esp-btn-sec" onClick={() => setNova(null)} disabled={salvando}>Cancelar</button>
              <button type="button" className="esp-btn" onClick={criarNova} disabled={salvando || !podeCriar}>
                {salvando
                  ? (progresso !== null ? `Enviando… ${progresso}%` : 'Criando…')
                  : nova.modo === 'pdf'
                    ? <><i className="bi bi-cloud-arrow-up" />Criar e enviar PDF</>
                    : <>Criar e escolher produtos<i className="bi bi-arrow-right" /></>}
              </button>
            </>
          )}
        >
          {erro && <div className="esp-erro"><i className="bi bi-exclamation-circle" />{erro}</div>}
          <label className="esp-rotulo mt-0">Nome do projeto</label>
          <input className="esp-campo" autoFocus maxLength={120} value={nova.nome} placeholder="Nome do projeto"
            onChange={e => setNova({ ...nova, nome: e.target.value })} disabled={salvando} />
          <label className="esp-rotulo">Cliente <span className="opc">opcional</span></label>
          <input className="esp-campo" maxLength={120} value={nova.cliente} placeholder="Nome do cliente"
            onChange={e => setNova({ ...nova, cliente: e.target.value })} disabled={salvando} />

          <label className="esp-rotulo">Como você quer montar a lista?</label>
          <div className="esp-modos" role="radiogroup">
            <button type="button" role="radio" aria-checked={nova.modo === 'catalogo'} className={`esp-modo ${nova.modo === 'catalogo' ? 'on' : ''}`}
              onClick={() => setNova({ ...nova, modo: 'catalogo' })} disabled={salvando}>
              <i className="bi bi-grid-1x2" />
              <div><strong>Escolher no catálogo</strong><span>Toque em + Especificar nos produtos. No fim, geramos o PDF com a sua logo.</span></div>
            </button>
            <button type="button" role="radio" aria-checked={nova.modo === 'pdf'} className={`esp-modo ${nova.modo === 'pdf' ? 'on' : ''}`}
              onClick={() => setNova({ ...nova, modo: 'pdf' })} disabled={salvando}>
              <i className="bi bi-cloud-arrow-up" />
              <div><strong>Enviar o PDF que já fiz</strong><span>Já tem o quantitativo pronto no seu programa? É só enviar o arquivo.</span></div>
            </button>
          </div>

          {nova.modo === 'pdf' && (
            <div className="mt-3">
              <EnvioPdf arquivo={nova.arquivo} progresso={progresso} desabilitado={salvando}
                onEscolher={f => setNova(n => ({ ...n, arquivo: f }))} />
            </div>
          )}
        </Folha>
      )}
    </div>
  );
}
